// CheckoutSuccessSpec — post-payment confirmation for the spec-driven (/s/:slug) path.
// Same polling logic as CheckoutSuccess.jsx but links back to /s/:slug.
// Themed to match the spec storefront rather than the platform UI.

import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

export default function CheckoutSuccessSpec() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending"); // pending | paid | expired
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("expired"); return; }
    let cancelled = false;
    const poll = async (n = 0) => {
      if (n >= 8 || cancelled) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setAttempts(n + 1);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const goBack = () => navigate(`/s/${slug}`);

  return (
    <div style={{
      minHeight: "100vh", display: "grid", placeItems: "center", padding: "2rem",
      fontFamily: "system-ui, sans-serif", background: "#fafaf9",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "3.5rem 3rem",
        maxWidth: 440, width: "100%", textAlign: "center",
        boxShadow: "0 4px 40px rgba(0,0,0,0.09)",
        border: "1px solid rgba(0,0,0,0.07)",
      }}>
        {status === "paid" ? (
          <>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
            <h1 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 0.6rem", letterSpacing: "-0.025em" }}>
              Order received!
            </h1>
            <p style={{ color: "#666", margin: "0 0 2rem", fontSize: "0.97rem" }}>
              Your order is confirmed and your kitchen has been notified.
            </p>
          </>
        ) : status === "expired" ? (
          <>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 900, margin: "0 0 0.6rem" }}>
              Session expired
            </h1>
            <p style={{ color: "#666", margin: "0 0 2rem", fontSize: "0.97rem" }}>
              The payment session timed out. Please try again.
            </p>
          </>
        ) : (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: "50%",
              border: "4px solid #e5e7eb", borderTopColor: "#111",
              animation: "spin 0.8s linear infinite", margin: "0 auto 1.25rem",
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 900, margin: "0 0 0.4rem" }}>
              Confirming payment…
            </h1>
            <p style={{ color: "#999", fontSize: "0.85rem", margin: "0 0 2rem" }}>
              Attempt {attempts}/8
            </p>
          </>
        )}

        <button
          onClick={goBack}
          style={{
            border: "2px solid #111", background: "transparent", color: "#111",
            fontWeight: 700, padding: "0.8rem 2rem", borderRadius: 10,
            cursor: "pointer", fontSize: "0.97rem", width: "100%",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#111"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#111"; }}
        >
          ← Back to menu
        </button>
      </div>
    </div>
  );
}
